// Автоконструктор: подбор показателей, территорий и лет, сборка черновиков виджетов.

import type { DashboardDefinition, EmissCatalogItem, Indicator, Territory, TerritoryLevel, VisualizationType, WidgetAppearance, WidgetDefinition, WorldBankCatalogItem } from '../types';
import { api } from '../api';
import { indicatorNames } from '../data-locale';
import { formatNumber as formatLocalizedNumber, t } from '../i18n';
import { isSnapshotVisualization } from '../data-utils';
import { normalizeDashboardTheme } from '../theme';

import type { AutoIndicatorChoice, AutoWidgetDraft, AutoWidgetRole, DashboardSourceType } from './state';
import { DEFAULT_APPEARANCE, blankDashboard, createClientId, normalizeAppearance, normalizeDashboard } from './model';
import { applyTheme, currentTheme } from './theming';
import { autoConfigureDashboardLayout } from './canvas';
import { controls } from './controls';
import { currentCollator, currentLocale, localizedUnitLabel } from './locale';
import { emissPlanProblem } from './emiss';
import { errorMessage, markDirty, setStatus, updateDashboardHeading } from './status';
import { escapeHtml } from './dom';
import { isMapVisualization } from './widget-form';
import { optionItemsFromTerritories, preserveSelectedOptions, searchNeedle, selectableOptionCount, selectedOrAvailableCount, selectedValues, setSelectOptions, updateChoiceCounter } from './choice-lists';
import { refreshDashboardList, saveDashboard } from './dashboards';
import { renderDashboardGrid } from './widgets';
import { setViewMode } from './workspace';
import { state } from './state';

export function defaultAutoTerritoryLevel(sourceType: DashboardSourceType): TerritoryLevel | undefined {
  if (sourceType === 'worldbank') return 'country';
  if (sourceType === 'emiss') return 'region';
  if (sourceType === 'settlements') return 'settlement';
  return 'municipality_upper';
}

export function autoRoleLayout(role: AutoWidgetRole): WidgetDefinition['layout'] {
  const layouts: Record<AutoWidgetRole, WidgetDefinition['layout']> = {
    kpi: { x: 0, y: 0, w: 3, h: 3 },
    trend: { x: 3, y: 0, w: 5, h: 4 },
    rank: { x: 8, y: 0, w: 4, h: 4 },
    map: { x: 0, y: 4, w: 7, h: 6 },
    matrix: { x: 7, y: 4, w: 5, h: 6 },
    table: { x: 0, y: 10, w: 12, h: 5 },
  };
  return { ...layouts[role] };
}

export function autoRoleVisualization(role: AutoWidgetRole, indicator: Indicator): VisualizationType | null {
  const allowed = new Set(indicator.allowed_visualizations?.length ? indicator.allowed_visualizations : [indicator.default_visualization]);
  if (role === 'kpi') return allowed.has('kpi') ? 'kpi' : null;
  if (role === 'map') {
    if (allowed.has('choropleth')) return 'choropleth';
    if (allowed.has('map_symbols')) return 'map_symbols';
    return null;
  }
  if (role === 'trend') {
    if (allowed.has('line')) return 'line';
    if (allowed.has('area')) return 'area';
    return null;
  }
  if (role === 'rank') return allowed.has('bar') ? 'bar' : null;
  if (role === 'matrix') return allowed.has('heatmap') ? 'heatmap' : null;
  return allowed.has('table') ? 'table' : null;
}

export function autoChoiceFromIndicator(indicator: Indicator): AutoIndicatorChoice {
  const metadata = indicator.source_metadata ?? {};
  const externalId = String(metadata.worldbank_indicator_id ?? metadata.indicator_id ?? indicator.id);
  return {
    key: `local:${indicator.id}`,
    sourceType: indicator.source_type as DashboardSourceType,
    kind: 'local',
    indicatorId: externalId,
    localIndicatorId: indicator.id,
    title: indicator.short_name || indicator.name,
    subtitle: indicator.id,
    unit: indicator.unit,
    indicator,
  };
}

export function autoChoiceFromEmiss(item: EmissCatalogItem): AutoIndicatorChoice {
  return {
    key: `emiss:${item.indicator_id}`,
    sourceType: 'emiss',
    kind: 'emiss_catalog',
    indicatorId: item.indicator_id,
    localIndicatorId: item.local_indicator_id,
    title: item.title,
    subtitle: `ЕМИСС ${item.indicator_id}`,
    unit: '',
    emissItem: item,
  };
}

export function autoChoiceFromWorldBank(item: WorldBankCatalogItem): AutoIndicatorChoice {
  return {
    key: `worldbank:${item.indicator_id}`,
    sourceType: 'worldbank',
    kind: 'worldbank_catalog',
    indicatorId: item.indicator_id,
    localIndicatorId: item.local_indicator_id,
    title: item.name || item.name_ru || item.name_en || item.indicator_id,
    subtitle: item.indicator_id,
    unit: item.unit,
    worldBankItem: item,
  };
}

export function selectedAutoIndicatorChoices(): AutoIndicatorChoice[] {
  const selected = state.autoIndicatorChoices.filter((choice) => state.autoSelectedIndicatorKeys.has(choice.key));
  return selected.length ? selected : [];
}

export function renderAutoIndicatorChoices(): void {
  const locale = currentLocale();
  const query = searchNeedle(controls.autoIndicatorSearch.value);
  const selected = state.autoSelectedIndicatorKeys;
  const visible = state.autoIndicatorChoices
    .filter((choice) => {
      if (!query) return true;
      return searchNeedle(`${choice.title} ${choice.subtitle} ${choice.unit} ${choice.indicatorId}`).includes(query);
    })
    .slice(0, 120);
  if (!visible.length) {
    controls.autoIndicatorChoiceList.innerHTML = `<p class="checkbox-choice-empty">${escapeHtml(t(locale, 'noDashboardsFound'))}</p>`;
  } else {
    controls.autoIndicatorChoiceList.innerHTML = visible.map((choice, index) => {
      const id = `auto-indicator-choice-${index}`;
      const checked = selected.has(choice.key) ? 'checked' : '';
      const unit = choice.unit ? `<small>${escapeHtml(localizedUnitLabel(choice.unit) || choice.unit)}</small>` : '';
      return `
        <label class="checkbox-choice auto-indicator-choice" for="${escapeHtml(id)}" title="${escapeHtml(`${choice.title} · ${choice.subtitle}`)}">
          <input id="${escapeHtml(id)}" type="checkbox" data-auto-indicator-key="${escapeHtml(choice.key)}" ${checked} />
          <span>
            <strong>${escapeHtml(choice.title)}</strong>
            <em>${escapeHtml(choice.subtitle)}</em>
            ${unit}
          </span>
        </label>
      `;
    }).join('');
  }
  const total = state.autoIndicatorChoices.length;
  const checked = selectedAutoIndicatorChoices().length;
  controls.autoIndicatorCounter.textContent = checked > 0
    ? t(locale, 'autoBuilderSelectedCount', {
      selected: formatLocalizedNumber(checked, locale, 0),
      total: formatLocalizedNumber(total, locale, 0),
    })
    : t(locale, 'autoBuilderAutoPick');
}

export function renderAutoSectionOptions(): void {
  const locale = currentLocale();
  const current = controls.autoSection.value;
  const query = searchNeedle(controls.autoSectionSearch.value);
  const sections = state.autoSections
    .filter((section) => section.count > 0)
    .filter((section) => !query || searchNeedle(`${section.title} ${section.id}`).includes(query));
  controls.autoSection.innerHTML = `<option value="">${escapeHtml(t(locale, 'allSections'))}</option>`
    + sections
      .map((section) => `<option value="${escapeHtml(section.id)}">${escapeHtml(section.title)} · ${section.count}</option>`)
      .join('');
  controls.autoSection.value = sections.some((section) => section.id === current) ? current : '';
}

export function fallbackAutoYears(sourceType: DashboardSourceType): number[] {
  const currentYear = new Date().getFullYear();
  if (sourceType === 'worldbank') {
    return Array.from({ length: currentYear - 1960 + 1 }, (_item, index) => 1960 + index);
  }
  if (sourceType === 'emiss') {
    return Array.from({ length: 15 }, (_item, index) => currentYear - 14 + index);
  }
  return [];
}

export async function ensureAutoWorldBankIndicator(item: WorldBankCatalogItem): Promise<Indicator | null> {
  if (item.registered && item.local_indicator_id) {
    const existing = state.indicators.find((indicator) => indicator.id === item.local_indicator_id);
    if (existing) return existing;
  }
  const registered = await api.registerWorldBank(item.indicator_id);
  if (!state.indicators.some((indicator) => indicator.id === registered.indicator.id)) {
    state.indicators.push(registered.indicator);
  }
  return registered.indicator;
}

export async function autoIndicatorFromChoice(choice: AutoIndicatorChoice): Promise<Indicator | null> {
  if (choice.kind === 'local') return choice.indicator ?? state.indicators.find((indicator) => indicator.id === choice.localIndicatorId) ?? null;
  if (choice.kind === 'emiss_catalog' && choice.emissItem) return ensureAutoEmissIndicator(choice.emissItem);
  if (choice.kind === 'worldbank_catalog' && choice.worldBankItem) return ensureAutoWorldBankIndicator(choice.worldBankItem);
  if (choice.localIndicatorId) {
    return state.indicators.find((indicator) => indicator.id === choice.localIndicatorId) ?? null;
  }
  return null;
}

export async function loadAutoIndicatorChoices(sourceType: DashboardSourceType, sectionId: string, requestToken: number): Promise<void> {
  const locale = currentLocale();
  const query = controls.autoIndicatorSearch.value.trim();
  let choices: AutoIndicatorChoice[] = [];
  if (sourceType === 'emiss') {
    let status = await api.emissCatalogStatus().catch(() => null);
    if (!status || status.total_count === 0) {
      controls.autoBuilderStatus.textContent = 'Каталог ЕМИСС пуст, загружаю список показателей...';
      status = await api.refreshEmissCatalog();
    }
    const result = await api.searchEmissCatalog(query, false, 80, 0, locale, sectionId);
    choices = result.items.map(autoChoiceFromEmiss);
  } else if (sourceType === 'worldbank') {
    let status = await api.worldBankCatalogStatus().catch(() => null);
    if (!status || status.total_count === 0) {
      controls.autoBuilderStatus.textContent = 'Каталог World Bank пуст, загружаю список показателей...';
      status = await api.refreshWorldBankCatalog();
    }
    const result = await api.searchWorldBankCatalog(query, 80, 0, locale, sectionId);
    choices = result.items.map(autoChoiceFromWorldBank);
  } else {
    const indicators = await api.indicators(true, locale, { sourceType, sectionId, q: query });
    choices = indicators.slice(0, 160).map(autoChoiceFromIndicator);
  }
  if (requestToken !== state.autoBuilderRequestToken) return;
  const validKeys = new Set(choices.map((choice) => choice.key));
  state.autoSelectedIndicatorKeys.forEach((key) => {
    if (!validKeys.has(key)) state.autoSelectedIndicatorKeys.delete(key);
  });
  state.autoIndicatorChoices = choices;
  renderAutoIndicatorChoices();
}

export function autoYearsForRole(role: AutoWidgetRole, availableYears: number[], selectedYears: number[]): number[] {
  const source = selectedYears.length
    ? availableYears.filter((year) => selectedYears.includes(year))
    : availableYears;
  const years = [...new Set(source)].filter(Number.isFinite).sort((a, b) => a - b);
  if (!years.length) return [];
  if (role === 'map' || role === 'rank') return [years.at(-1)!];
  if (role === 'table') return years.slice(-6);
  if (role === 'matrix') return years.slice(-10);
  return years.slice(-12);
}

export function autoTerritoriesForRole(role: AutoWidgetRole, territories: Territory[], selectedIds: string[]): string[] {
  const source = selectedIds.length
    ? territories.filter((territory) => selectedIds.includes(territory.id))
    : territories.filter((territory) => territory.selectable !== false);
  const ids = source.map((territory) => territory.id);
  if (role === 'kpi') return ids.slice(0, 1);
  if (role === 'trend') return ids.slice(0, 6);
  if (role === 'matrix') return ids.slice(0, 18);
  if (role === 'table') return ids.slice(0, 30);
  return ids.slice(0, 89);
}

export async function autoDimensions(indicator: Indicator, visualization: VisualizationType): Promise<Record<string, string[]> | null> {
  const dimensions = await api.dimensions(indicator.id, currentLocale());
  const result: Record<string, string[]> = {};
  for (const dimension of dimensions) {
    const defaults = [...(dimension.default_values ?? [])].filter(Boolean);
    if (defaults.length) {
      result[dimension.id] = isMapVisualization(visualization) ? defaults.slice(0, 1) : defaults;
    } else if (dimension.required || isMapVisualization(visualization)) {
      return null;
    }
  }
  return result;
}

export function autoAppearance(role: AutoWidgetRole, visualization: VisualizationType): WidgetAppearance {
  return normalizeAppearance({
    ...DEFAULT_APPEARANCE,
    palette: 'dashboard',
    showLegend: role !== 'kpi',
    mapLegendMode: 'collapsed',
    kpiIconMode: 'none',
    tableCellVisual: role === 'table' ? 'bars' : DEFAULT_APPEARANCE.tableCellVisual,
    orientation: role === 'rank' ? 'horizontal' : DEFAULT_APPEARANCE.orientation,
    topN: role === 'rank' ? 10 : 0,
    dataZoom: role === 'trend' ? 'never' : DEFAULT_APPEARANCE.dataZoom,
    smoothLines: visualization === 'line' || visualization === 'area',
  });
}

export function autoWidgetTitleEn(role: AutoWidgetRole, indicator: Indicator): string {
  const prefixes: Record<AutoWidgetRole, string> = {
    kpi: 'Key value',
    map: 'Map',
    trend: 'Trend',
    rank: 'Ranking',
    matrix: 'Territories × years',
    table: 'Data',
  };
  return `${prefixes[role]}: ${indicatorNames(indicator).en}`;
}

export function autoWidgetTitle(role: AutoWidgetRole, indicator: Indicator): string {
  const name = indicatorNames(indicator).ru;
  const prefixes: Record<AutoWidgetRole, string> = {
    kpi: 'Ключевое значение',
    map: 'Карта',
    trend: 'Динамика',
    rank: 'Рейтинг',
    matrix: 'Территории × годы',
    table: 'Данные',
  };
  return `${prefixes[role]}: ${name}`;
}

export async function ensureAutoEmissIndicator(item: EmissCatalogItem): Promise<Indicator | null> {
  if (item.registered && item.local_indicator_id) {
    const existing = state.indicators.find((indicator) => indicator.id === item.local_indicator_id);
    if (existing) return existing;
  }
  const inspection = await api.inspectEmiss(item.indicator_id, { forceRefresh: false });
  const registered = await api.registerEmiss({
    indicator_id: item.indicator_id,
    name: inspection.indicator_title,
    unit: 'ед.',
    frequency: inspection.frequency,
    default_visualization: 'line',
    allowed_visualizations: ['kpi', 'line', 'bar', 'area', 'heatmap', 'table', 'choropleth'],
    field_map: inspection.field_map,
    fixed_filters: inspection.fixed_filters,
    dimension_defaults: {},
    fetch_strategy: 'full_page',
    cache_ttl_seconds: 86400,
    active: true,
    overwrite: false,
    force_refresh: false,
  });
  if (!state.indicators.some((indicator) => indicator.id === registered.indicator.id)) {
    state.indicators.push(registered.indicator);
  }
  return registered.indicator;
}

export async function autoIndicatorCandidates(
  sourceType: DashboardSourceType,
  sectionId: string,
  manualChoices: AutoIndicatorChoice[],
  autoFillMissingRoles: boolean,
): Promise<Indicator[]> {
  const indicators: Indicator[] = [];
  for (const choice of manualChoices) {
    try {
      const indicator = await autoIndicatorFromChoice(choice);
      if (indicator && !indicators.some((candidate) => candidate.id === indicator.id)) indicators.push(indicator);
    } catch {
      // Один выбранный показатель не подключился — автосборщик продолжает с
      // остальными, а не падает целиком (CODE_AUDIT 7.6).
    }
  }
  if (manualChoices.length && !autoFillMissingRoles) return indicators.slice(0, 18);
  if (sourceType !== 'emiss') {
    if (sourceType === 'worldbank') {
      const search = await api.searchWorldBankCatalog('', 12, 0, currentLocale(), sectionId);
      for (const item of search.items) {
        if (indicators.length >= 18) break;
        try {
          const indicator = await ensureAutoWorldBankIndicator(item);
          if (indicator && !indicators.some((candidate) => candidate.id === indicator.id)) indicators.push(indicator);
        } catch {
          // Один показатель World Bank не подключился — продолжаем со следующим.
        }
      }
      return indicators.slice(0, 18);
    }
    const local = await api.indicators(true, currentLocale(), { sourceType, sectionId });
    for (const indicator of local) {
      if (!indicators.some((candidate) => candidate.id === indicator.id)) indicators.push(indicator);
      if (indicators.length >= 18) break;
    }
    return indicators.slice(0, 18);
  }
  let status = await api.emissCatalogStatus().catch(() => null);
  if (!status || status.total_count === 0) {
    controls.autoBuilderStatus.textContent = 'Каталог ЕМИСС пуст, обновляю список показателей...';
    status = await api.refreshEmissCatalog();
  }
  const search = await api.searchEmissCatalog('', false, 8, 0, currentLocale(), sectionId);
  for (const item of search.items.slice(0, 8)) {
    try {
      controls.autoBuilderStatus.textContent = `Подключаю ЕМИСС ${item.indicator_id}: ${item.title}`;
      const indicator = await ensureAutoEmissIndicator(item);
      if (indicator && !indicators.some((candidate) => candidate.id === indicator.id)) indicators.push(indicator);
      if (indicators.length >= 6) break;
    } catch {
      // Один показатель ЕМИСС не подключился — продолжаем со следующим.
    }
  }
  const [indicatorsCatalog, territories] = await Promise.all([
    api.indicators(true, currentLocale()),
    api.territories({}, currentLocale()),
  ]);
  state.indicators = indicatorsCatalog;
  state.localTerritories = territories;
  state.catalogTerritories = territories;
  return indicators;
}

export async function autoTerritoryCandidates(sourceType: DashboardSourceType, indicatorId?: string): Promise<Territory[]> {
  const level = defaultAutoTerritoryLevel(sourceType);
  let territories = await api.territories({
    sourceType,
    indicatorId,
    level,
    limit: sourceType === 'settlements' ? 120 : 500,
  }, currentLocale());
  if (!territories.length && level) {
    territories = await api.territories({ sourceType, indicatorId, limit: 500 }, currentLocale());
  }
  return territories.filter((territory) => {
    if (territory.selectable === false) return false;
    if (sourceType === 'emiss') return territory.level === 'region' && territory.id.startsWith('ru_region:');
    return true;
  });
}

export async function autoReferenceIndicatorId(sourceType: DashboardSourceType): Promise<string | undefined> {
  const selected = selectedAutoIndicatorChoices();
  const localChoice = selected.find((choice) => choice.localIndicatorId) ?? state.autoIndicatorChoices.find((choice) => choice.localIndicatorId);
  if (localChoice?.localIndicatorId) return localChoice.localIndicatorId;
  const localIndicator = state.autoIndicatorChoices.find((choice) => choice.indicator)?.indicator;
  if (localIndicator) return localIndicator.id;
  const first = (await api.indicators(true, currentLocale(), {
    sourceType,
    sectionId: controls.autoSection.value,
    q: controls.autoIndicatorSearch.value.trim(),
  }))[0];
  return first?.id;
}

export async function refreshAutoBdmoTerritories(requestToken: number): Promise<void> {
  const indicatorId = await autoReferenceIndicatorId('bdmo');
  controls.autoBdmoTerritoryPanel.hidden = false;
  controls.autoGenericTerritoryPanel.hidden = true;
  if (!indicatorId || requestToken !== state.autoBuilderRequestToken) return;
  const districts = await api.territories({ sourceType: 'bdmo', level: 'federal_district', limit: 100 }, currentLocale());
  if (requestToken !== state.autoBuilderRequestToken) return;
  state.autoBdmoDistricts = districts;
  const districtItems = optionItemsFromTerritories(districts);
  setSelectOptions(controls.autoDistricts, districtItems, preserveSelectedOptions(controls.autoDistricts, districtItems));
  updateChoiceCounter(controls.autoDistricts, controls.autoDistrictSearch, controls.autoDistrictCounter);

  const selectedDistrictIds = selectedValues(controls.autoDistricts);
  const regionGroups = selectedDistrictIds.length
    ? await Promise.all(selectedDistrictIds.map((federalDistrictId) => api.territories({
      sourceType: 'bdmo',
      indicatorId,
      level: 'region',
      federalDistrictId,
      search: controls.autoRegionSearch.value.trim(),
      limit: 200,
    }, currentLocale())))
    : [await api.territories({
      sourceType: 'bdmo',
      indicatorId,
      level: 'region',
      search: controls.autoRegionSearch.value.trim(),
      limit: 300,
    }, currentLocale())];
  if (requestToken !== state.autoBuilderRequestToken) return;
  const regionMap = new Map(regionGroups.flat().map((territory) => [territory.id, territory]));
  state.autoBdmoRegions = [...regionMap.values()].sort((a, b) => currentCollator().compare(a.name, b.name));
  const regionItems = optionItemsFromTerritories(state.autoBdmoRegions);
  setSelectOptions(controls.autoRegions, regionItems, preserveSelectedOptions(controls.autoRegions, regionItems));
  updateChoiceCounter(controls.autoRegions, controls.autoRegionSearch, controls.autoRegionCounter);

  const selectedRegionIds = selectedValues(controls.autoRegions);
  const municipalityRequests = selectedRegionIds.length
    ? selectedRegionIds.map((regionId) => api.territories({
      sourceType: 'bdmo',
      indicatorId,
      level: 'municipality_upper',
      regionId,
      search: controls.autoMunicipalitySearch.value.trim(),
      limit: 1000,
    }, currentLocale()))
    : selectedDistrictIds.length
      ? selectedDistrictIds.map((federalDistrictId) => api.territories({
        sourceType: 'bdmo',
        indicatorId,
        level: 'municipality_upper',
        federalDistrictId,
        search: controls.autoMunicipalitySearch.value.trim(),
        limit: 1500,
      }, currentLocale()))
      : [api.territories({
        sourceType: 'bdmo',
        indicatorId,
        level: 'municipality_upper',
        search: controls.autoMunicipalitySearch.value.trim(),
        limit: 5000,
      }, currentLocale())];
  const municipalityGroups = await Promise.all(municipalityRequests);
  if (requestToken !== state.autoBuilderRequestToken) return;
  const municipalityMap = new Map(municipalityGroups.flat().map((territory) => [territory.id, territory]));
  state.autoBdmoMunicipalities = [...municipalityMap.values()].sort((a, b) => currentCollator().compare(a.name, b.name));
  const municipalityItems = optionItemsFromTerritories(state.autoBdmoMunicipalities);
  setSelectOptions(
    controls.autoMunicipalities,
    municipalityItems,
    preserveSelectedOptions(controls.autoMunicipalities, municipalityItems),
  );
  updateChoiceCounter(controls.autoMunicipalities, controls.autoMunicipalitySearch, controls.autoMunicipalityCounter);
  updateAutoBuilderSelectionPreview();
}

export async function refreshAutoGenericTerritories(sourceType: DashboardSourceType, requestToken: number): Promise<void> {
  controls.autoBdmoTerritoryPanel.hidden = true;
  controls.autoGenericTerritoryPanel.hidden = false;
  const indicatorId = await autoReferenceIndicatorId(sourceType);
  if (requestToken !== state.autoBuilderRequestToken) return;
  const territories = await autoTerritoryCandidates(sourceType, indicatorId);
  if (requestToken !== state.autoBuilderRequestToken) return;
  const items = optionItemsFromTerritories(territories.slice(0, sourceType === 'settlements' ? 300 : 500));
  setSelectOptions(controls.autoTerritories, items, preserveSelectedOptions(controls.autoTerritories, items));
  updateChoiceCounter(controls.autoTerritories, controls.autoTerritorySearch, controls.autoTerritoryCounter);
  updateAutoBuilderSelectionPreview();
}

export function selectedAutoTerritoryIds(sourceType: DashboardSourceType): string[] {
  if (sourceType !== 'bdmo') return selectedValues(controls.autoTerritories);
  const selectedMunicipalities = selectedValues(controls.autoMunicipalities);
  if (selectedMunicipalities.length) return selectedMunicipalities;
  const hasFilter = selectedValues(controls.autoDistricts).length > 0 || selectedValues(controls.autoRegions).length > 0;
  if (!hasFilter) return [];
  return Array.from(controls.autoMunicipalities.options)
    .filter((option) => !option.disabled)
    .map((option) => option.value);
}

export async function refreshAutoYears(sourceType: DashboardSourceType, requestToken: number): Promise<void> {
  const choices = selectedAutoIndicatorChoices().length
    ? selectedAutoIndicatorChoices()
    : state.autoIndicatorChoices.slice(0, 8);
  const years = new Set<number>();
  let preliminary = false;
  for (const choice of choices) {
    const localId = choice.localIndicatorId ?? choice.indicator?.id ?? null;
    if (localId) {
      try {
        const periods = await api.periods(localId, currentLocale());
        periods.years.forEach((year) => years.add(year));
        if (periods.source?.includes('fallback')) preliminary = true;
        continue;
      } catch {}
    }
    fallbackAutoYears(sourceType).forEach((year) => years.add(year));
    if (sourceType === 'emiss' || sourceType === 'worldbank') preliminary = true;
  }
  if (!years.size) fallbackAutoYears(sourceType).forEach((year) => years.add(year));
  if (requestToken !== state.autoBuilderRequestToken) return;
  const items = [...years]
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .map((year) => ({ value: String(year), label: String(year) }));
  setSelectOptions(controls.autoYears, items, preserveSelectedOptions(controls.autoYears, items));
  updateChoiceCounter(controls.autoYears, controls.autoYearSearch, controls.autoYearCounter);
  controls.autoYearHelp.textContent = preliminary
    ? t(currentLocale(), 'autoBuilderPreliminaryYears')
    : t(currentLocale(), 'autoBuilderSafeDefault');
  updateAutoBuilderSelectionPreview();
}

export function updateAutoBuilderSelectionPreview(): void {
  const locale = currentLocale();
  const sourceType = controls.autoSource.value as DashboardSourceType;
  const selectedIndicatorCount = selectedAutoIndicatorChoices().length;
  const indicatorCount = selectedIndicatorCount
    ? formatLocalizedNumber(selectedIndicatorCount, locale, 0)
    : controls.autoBuilderStatus.dataset.indicators
    ?? (sourceType === 'emiss' ? 'до 8' : '0');
  const widgetCount = controls.autoBuilderStatus.dataset.widgets
    ?? (sourceType === 'emiss' ? '6' : '0');
  const territoryIds = selectedAutoTerritoryIds(sourceType);
  const territoryCount = territoryIds.length
    || (sourceType === 'bdmo' ? selectableOptionCount(controls.autoMunicipalities) : selectedOrAvailableCount(controls.autoTerritories));
  const yearCount = selectedOrAvailableCount(controls.autoYears);
  const hasExplicitTerritories = territoryIds.length > 0;
  const hasExplicitYears = selectedValues(controls.autoYears).length > 0;
  const needsAutoHint = !hasExplicitTerritories || !hasExplicitYears;
  controls.autoBuilderStatus.textContent = `${t(locale, 'autoBuilderPreview', {
    indicators: indicatorCount,
    territories: territoryCount ? formatLocalizedNumber(territoryCount, locale, 0) : t(locale, 'auto'),
    years: yearCount ? formatLocalizedNumber(yearCount, locale, 0) : t(locale, 'auto'),
    widgets: widgetCount,
  })}${needsAutoHint ? ` ${t(locale, 'autoBuilderSafeDefault')}` : ''}`;
}

export async function buildAutoWidgetDrafts(
  sourceType: DashboardSourceType,
  sectionId: string,
  selectedTerritoryIds: string[],
  selectedYears: number[],
  manualChoices: AutoIndicatorChoice[],
  autoFillMissingRoles: boolean,
): Promise<{ drafts: AutoWidgetDraft[]; skipped: string[] }> {
  const indicators = await autoIndicatorCandidates(sourceType, sectionId, manualChoices, autoFillMissingRoles);
  const roles: AutoWidgetRole[] = ['kpi', 'trend', 'rank', 'map', 'matrix', 'table'];
  const drafts: AutoWidgetDraft[] = [];
  const skipped: string[] = [];
  let candidateIndex = 0;
  if (!indicators.length) {
    return { drafts, skipped: ['В выбранном разделе нет доступных показателей.'] };
  }
  for (const role of roles) {
    let accepted = false;
    for (let attempts = 0; attempts < indicators.length; attempts += 1) {
      const indicator = indicators[(candidateIndex + attempts) % indicators.length]!;
      const visualization = autoRoleVisualization(role, indicator);
      if (!visualization) {
        skipped.push(`${indicator.name}: нет подходящего типа для роли ${role}`);
        continue;
      }
      try {
        const [periods, territories, dimensions] = await Promise.all([
          api.periods(indicator.id, currentLocale()),
          autoTerritoryCandidates(sourceType, indicator.id),
          autoDimensions(indicator, visualization),
        ]);
        if (!dimensions) {
          skipped.push(`${indicator.name}: нужно выбрать значения разрезов — итог не подставляется автоматически`);
          continue;
        }
        const years = autoYearsForRole(role, periods.years, selectedYears);
        const territoryIds = autoTerritoriesForRole(role, territories, selectedTerritoryIds);
        if (!years.length || !territoryIds.length) {
          skipped.push(`${indicator.name}: нет пересечения годов или территорий`);
          continue;
        }
        const draft = { role, indicator, visualization, layout: autoRoleLayout(role), territoryIds, years, dimensions };
        if (indicator.source_type === 'emiss') {
          const plan = await api.planEmissQuery(indicator.id, {
            indicatorId: indicator.id,
            territoryIds,
            years,
            months: [],
            dimensions,
          });
          const problem = emissPlanProblem(plan);
          if (problem) {
            skipped.push(`${indicator.name}: ${problem}`);
            continue;
          }
        }
        const probe = await api.queryData({
          indicatorId: indicator.id,
          compareIndicatorIds: [],
          territoryIds,
          years,
          months: [],
          dimensions,
        }, false, currentLocale());
        if (!probe.rows.length) {
          skipped.push(`${indicator.name}: нет строк данных для выбранной выборки`);
          continue;
        }
        drafts.push(draft);
        candidateIndex = (candidateIndex + attempts + 1) % Math.max(1, indicators.length);
        accepted = true;
        break;
      } catch (error) {
        skipped.push(`${indicator.name}: ${errorMessage(error)}`);
      }
    }
    if (!accepted && drafts.length >= 4) break;
  }
  return { drafts, skipped };
}

export function autoDefinitionFromDrafts(title: string, primaryColor: string, drafts: AutoWidgetDraft[]): DashboardDefinition {
  return {
    ...blankDashboard(title),
    theme: normalizeDashboardTheme({ primaryColor }),
    widgets: drafts.map((draft) => ({
      id: createClientId(),
      title: autoWidgetTitle(draft.role, draft.indicator),
      titleEn: autoWidgetTitleEn(draft.role, draft.indicator),
      visualization: draft.visualization,
      query: {
        indicatorId: draft.indicator.id,
        compareIndicatorIds: [],
        territoryIds: draft.territoryIds,
        years: draft.years,
        months: [],
        dimensions: draft.dimensions,
      },
      appearance: autoAppearance(draft.role, draft.visualization),
      time: { activePeriod: null, showControl: isSnapshotVisualization(draft.visualization) },
      layout: draft.layout,
    })),
  };
}

export async function refreshAutoBuilderOptions(): Promise<void> {
  const sourceType = controls.autoSource.value as DashboardSourceType;
  const locale = currentLocale();
  const requestToken = ++state.autoBuilderRequestToken;
  controls.autoBuilderStatus.textContent = t(locale, 'autoBuilderLoading');
  delete controls.autoBuilderStatus.dataset.indicators;
  delete controls.autoBuilderStatus.dataset.widgets;
  controls.autoSection.disabled = true;
  controls.autoTerritories.innerHTML = '';
  controls.autoYears.innerHTML = '';
  controls.autoIndicatorChoiceList.innerHTML = '';
  controls.autoDistricts.innerHTML = '';
  controls.autoRegions.innerHTML = '';
  controls.autoMunicipalities.innerHTML = '';
  try {
    state.autoSections = await api.indicatorSections(sourceType, locale);
    if (requestToken !== state.autoBuilderRequestToken) return;
    renderAutoSectionOptions();
    await loadAutoIndicatorChoices(sourceType, controls.autoSection.value, requestToken);
    if (requestToken !== state.autoBuilderRequestToken) return;
    if (sourceType === 'bdmo') {
      await refreshAutoBdmoTerritories(requestToken);
    } else {
      await refreshAutoGenericTerritories(sourceType, requestToken);
    }
    if (requestToken !== state.autoBuilderRequestToken) return;
    await refreshAutoYears(sourceType, requestToken);
    if (requestToken !== state.autoBuilderRequestToken) return;
    const candidateCount = sourceType === 'emiss'
      ? 'до 8'
      : formatLocalizedNumber(
        selectedAutoIndicatorChoices().length || state.autoIndicatorChoices.length,
        locale,
        0,
      );
    controls.autoBuilderStatus.dataset.indicators = candidateCount;
    controls.autoBuilderStatus.dataset.widgets = String(sourceType === 'emiss'
      ? 6
      : Math.min(10, Math.max(3, selectedAutoIndicatorChoices().length || state.autoIndicatorChoices.length)));
    updateAutoBuilderSelectionPreview();
  } catch (error) {
    if (requestToken !== state.autoBuilderRequestToken) return;
    delete controls.autoBuilderStatus.dataset.indicators;
    delete controls.autoBuilderStatus.dataset.widgets;
    controls.autoBuilderStatus.textContent = `Не удалось подготовить автоконструктор: ${errorMessage(error)}`;
  } finally {
    if (requestToken === state.autoBuilderRequestToken) {
      controls.autoSection.disabled = false;
    }
  }
}

export function openAutoBuilder(): void {
  controls.autoPrimaryColor.value = currentTheme().primaryColor;
  controls.autoBuilderStatus.textContent = t(currentLocale(), 'autoBuilderReady');
  controls.autoBuilderDialog.showModal();
  void refreshAutoBuilderOptions();
}

export function closeAutoBuilder(): void {
  controls.autoBuilderDialog.close();
}

export async function createAutoDashboard(): Promise<void> {
  const sourceType = controls.autoSource.value as DashboardSourceType;
  const sectionId = controls.autoSection.value;
  const territoryIds = selectedAutoTerritoryIds(sourceType);
  const years = selectedValues(controls.autoYears).map(Number).filter(Number.isFinite);
  const manualChoices = selectedAutoIndicatorChoices();
  if (!controls.autoPickEnabled.checked && manualChoices.length === 0) {
    controls.autoBuilderStatus.textContent = currentLocale() === 'ru'
      ? 'Отметьте показатели вручную или включите автоподбор.'
      : 'Select indicators manually or enable auto-pick.';
    return;
  }
  const primaryColor = controls.autoPrimaryColor.value || currentTheme().primaryColor;
  controls.autoBuilderCreate.disabled = true;
  controls.autoBuilderStatus.textContent = 'Подбираю показатели и визуализации...';
  try {
    const { drafts, skipped } = await buildAutoWidgetDrafts(
      sourceType,
      sectionId,
      territoryIds,
      years,
      manualChoices,
      manualChoices.length ? controls.autoFillMissing.checked : controls.autoPickEnabled.checked,
    );
    if (drafts.length < 3) {
      controls.autoBuilderStatus.textContent = `Автоконструктор нашёл только ${drafts.length} валидных виджета. ${skipped.slice(0, 3).join(' ')}`;
      return;
    }
    const sourceLabel = controls.autoSource.selectedOptions[0]?.textContent?.trim() || sourceType;
    const sectionLabel = controls.autoSection.selectedOptions[0]?.textContent?.replace(/·\s*\d+$/, '').trim() || 'все разделы';
    const title = `Автодашборд: ${sourceLabel} · ${sectionLabel}`;
    const definition = autoDefinitionFromDrafts(title, primaryColor, drafts.slice(0, 6));
    const saved = await api.createDashboard(title, definition);
    state.currentDashboardId = saved.id;
    state.currentTemplateId = null;
    state.definition = normalizeDashboard(saved.definition, saved.name);
    controls.dashboardName.value = saved.name;
    applyTheme();
    await refreshDashboardList();
    updateDashboardHeading();
    renderDashboardGrid();
    markDirty(false);
    setViewMode(false);
    closeAutoBuilder();
    setStatus(`Автоконструктор создал дашборд «${saved.name}»`, 'success');
    requestAnimationFrame(() => {
      autoConfigureDashboardLayout();
      void saveDashboard();
    });
  } catch (error) {
    controls.autoBuilderStatus.textContent = `Автоконструктор не смог создать дашборд: ${errorMessage(error)}`;
  } finally {
    controls.autoBuilderCreate.disabled = false;
  }
}
